import { useEffect, useMemo, useState } from "react";

import { observer } from "mobx-react-lite";

import { ModalOpenPDF, type DrawingRotation, type SelectionCoords } from "@synthesis/pdf-viewer";
import { IconDeleteForever, IconDownload, IconEdit, IconFileInfo, IconLoader, IconSelectOnModel, IconSelectWithFrame } from "@synthesis/shared-icons";
import { useFindByProjectId } from "@synthesis/shared-services/models/modelsDrawingsController/hooks";
import { Button, SearchBar, Tooltip, setSnack } from "@synthesis/shared-ui";
import { cn, logger } from "@synthesis/shared-utils";

import { saveBlobAsFile } from "../../../../../shared/utils";
import type { FileMetaDto } from "../../api";
import { downloadDrawingsZip, downloadPdfPages } from "../../api";
import SortDropdown from "../../hooks/local-component/sort-dropdown";
import { useStores } from "../../store";
import { ModalReName } from "../explorer/local-components";
import type { ContextMenuStateFunctions } from "../files/hooks";
import { TableView } from "../files/local-components";
import {
  DEFAULT_PDF_PAGE,
  DRAWINGS_ZIP_FILE_NAME,
  EMPTY_PAGE_COUNT,
  MIN_DRAWINGS_FOR_ZIP,
  buildSinglePageDownloads,
  findDrawingById,
  getDownloadErrorTitle,
  getDownloadableDrawings,
  getSelectedDrawings,
  mapDrawingList,
  toBindDrawingTargets,
} from "./drawings-collection.lib";
import { useDrawingsCollectionModals } from "./hooks";
import { ModalBindToModel, ModalDeleteDrawing, ModalDrawingInfo } from "./local-components";

type DrawingsCollectionProps = {
  disabledRight?: boolean;
};

const noopFileHandler = (_file?: FileMetaDto) => undefined;
const noopHandler = () => undefined;

export const DrawingsCollection = observer(({ disabledRight }: DrawingsCollectionProps) => {
  const { generalStore, fileStore } = useStores();
  const { data: drawingsData, isLoading: isDrawingsLoading } = useFindByProjectId(
    { path: { projectId: generalStore.projectId ?? "" } },
    { query: { enabled: !!generalStore.projectId } },
  );

  const [search, setSearch] = useState("");
  const [, setSelectedFile] = useState<FileMetaDto | undefined>();
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfModalDrawingId, setPdfModalDrawingId] = useState("");
  const [pdfModalFileId, setPdfModalFileId] = useState("");
  const [pdfModalFileName, setPdfModalFileName] = useState("");
  const [pdfModalPage, setPdfModalPage] = useState(DEFAULT_PDF_PAGE);
  const [pdfModalHighlightArea, setPdfModalHighlightArea] = useState<SelectionCoords | undefined>(undefined);
  const [pdfModalRotation, setPdfModalRotation] = useState<DrawingRotation>(0);

  const drawings = useMemo(() => mapDrawingList(drawingsData), [drawingsData]);

  const hasSelection = generalStore.checkedFiles.size > 0;
  const hasSingleSelection = generalStore.checkedFiles.size === 1;
  const selectedDrawingIds = Array.from(generalStore.checkedFiles.keys());
  const selectedDrawingId = selectedDrawingIds[0];
  const isDownloadDisabled = !hasSelection || !!disabledRight || fileStore.isDownloading;

  const selectedDrawingsForBind = getSelectedDrawings(drawings, selectedDrawingIds);
  const selectedDrawing = selectedDrawingsForBind.find((drawing) => drawing.id === selectedDrawingId) ?? selectedDrawingsForBind[0];
  const selectedDrawingName = selectedDrawing?.fileName ?? "";

  const {
    isBindOpen,
    setIsBindOpen,
    isRenameOpen,
    setIsRenameOpen,
    isDeleteOpen,
    setIsDeleteOpen,
    isInfoOpen,
    setIsInfoOpen,
    openBindModal,
    openRenameModal,
    openDeleteModal,
    openInfoModal,
  } = useDrawingsCollectionModals({
    disabledRight,
    selectedCount: generalStore.checkedFiles.size,
    selectedDrawingId,
    drawings,
  });

  const openDefineModal = (file?: FileMetaDto) => {
    if (disabledRight) {
      return;
    }

    const drawingId = file?.id ?? selectedDrawingId;
    const target = findDrawingById(drawings, drawingId);

    if (!target) {
      return;
    }

    setPdfModalDrawingId(target.id);
    setPdfModalFileId(target.pdfId || "");
    setPdfModalFileName(target.fileName);
    setPdfModalPage(target.page || DEFAULT_PDF_PAGE);
    setPdfModalHighlightArea(target.highlightArea);
    setPdfModalRotation(target.rotation);
    setIsPdfModalOpen(true);
  };

  const handleDownload = async () => {
    if (generalStore.checkedFiles.size === 0 || fileStore.isDownloading) {
      return;
    }

    const drawingsToDownload = getDownloadableDrawings(drawings, selectedDrawingIds);

    if (drawingsToDownload.length === 0) {
      return;
    }

    fileStore.isDownloading = true;

    try {
      if (drawingsToDownload.length >= MIN_DRAWINGS_FOR_ZIP) {
        const blobData = await downloadDrawingsZip(drawingsToDownload.map((drawing) => drawing.id));
        saveBlobAsFile(blobData, DRAWINGS_ZIP_FILE_NAME);
        return;
      }

      const downloads = await Promise.all(
        buildSinglePageDownloads(drawingsToDownload).map(async ({ pdfId, page, fileName }) => {
          const blobData = await downloadPdfPages(pdfId, String(page), { responseType: "blob" });

          return { blobData, fileName };
        }),
      );

      for (const { blobData, fileName } of downloads) {
        saveBlobAsFile(blobData, fileName);
      }
    } catch (err) {
      logger.error("Error downloading drawings:", err);
      setSnack({
        title: getDownloadErrorTitle(drawingsToDownload.length),
        messageType: "error",
      });
    } finally {
      fileStore.isDownloading = false;
    }
  };

  const stateFunctions: ContextMenuStateFunctions = {
    openViewer: noopFileHandler,
    openDoc: noopFileHandler,
    handleClickReName: openRenameModal,
    handleDeleteFile: openDeleteModal,
    setIsMarkupOpen: noopHandler,
    handleClickAddNewVersion: noopHandler,
    setIsOpenMeta: noopHandler,
    handleClickConvert: noopHandler,
    handleDefineDrawing: openDefineModal,
    handleBindToModel: openBindModal,
    handleDownloadDrawing: handleDownload,
    handleOpenDrawingInfo: openInfoModal,
  };

  const pdfModalDrawing = findDrawingById(drawings, pdfModalDrawingId) ?? selectedDrawing;

  useEffect(() => {
    if (!isPdfModalOpen || !pdfModalDrawing) {
      return;
    }

    setPdfModalFileId(pdfModalDrawing.pdfId || "");
    setPdfModalFileName(pdfModalDrawing.fileName);
    setPdfModalPage(pdfModalDrawing.page || DEFAULT_PDF_PAGE);
    setPdfModalHighlightArea(pdfModalDrawing.highlightArea);
    setPdfModalRotation(pdfModalDrawing.rotation);
  }, [isPdfModalOpen, pdfModalDrawing]);

  return (
    <section className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <Button
            size="sm"
            theme="secondary"
            leftIcon={<IconSelectWithFrame className="size-5" />}
            disabled={!hasSingleSelection || disabledRight}
            onClick={() => openDefineModal()}
          >
            Определить/Изменить
          </Button>
          <div className="bg-neutrals-95 h-8 w-px" />
          <Tooltip description="Привязать к модели" arrowSide="bottom">
            <span className="inline-flex">
              <Button
                size="sm"
                theme="text"
                icon={<IconSelectOnModel className="size-5" />}
                disabled={!hasSelection || disabledRight}
                onClick={openBindModal}
                aria-label="Привязать к модели"
              />
            </span>
          </Tooltip>
          <div className="bg-neutrals-95 h-8 w-px" />
          <Tooltip description="Переименовать" arrowSide="bottom">
            <span className="inline-flex">
              <Button
                size="sm"
                theme="text"
                icon={<IconEdit className="size-5" />}
                disabled={!hasSingleSelection || disabledRight}
                onClick={openRenameModal}
                aria-label="Переименовать"
              />
            </span>
          </Tooltip>
          <Tooltip description="Информация" arrowSide="bottom">
            <span className="inline-flex">
              <Button
                size="sm"
                theme="text"
                icon={<IconFileInfo className="size-5" />}
                disabled={!hasSingleSelection}
                onClick={openInfoModal}
                aria-label="Информация"
              />
            </span>
          </Tooltip>
          <Tooltip description="Скачать" arrowSide="bottom">
            <span className={cn("inline-flex", fileStore.isDownloading && "cursor-not-allowed")}>
              <Button
                size="sm"
                theme="text"
                className={fileStore.isDownloading ? "cursor-not-allowed" : undefined}
                icon={fileStore.isDownloading ? <IconLoader className="size-5 animate-spin" aria-hidden /> : <IconDownload className="size-5" />}
                disabled={isDownloadDisabled}
                onClick={handleDownload}
                aria-label="Скачать"
              />
            </span>
          </Tooltip>
          <div className="bg-neutrals-95 h-8 w-px" />
          <Tooltip description="Удалить" arrowSide="bottom">
            <span className="inline-flex">
              <Button
                size="sm"
                theme="text"
                icon={<IconDeleteForever className="size-5" />}
                disabled={!hasSelection || disabledRight}
                onClick={openDeleteModal}
                aria-label="Удалить"
              />
            </span>
          </Tooltip>
        </div>

        <div className="flex items-start gap-2">
          <SortDropdown />
          <SearchBar size="sm" className="min-w-[240px]" clearable onChange={setSearch} />
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <TableView
          variant="drawings-collection"
          onClickModel={noopFileHandler}
          stateFunctions={stateFunctions}
          setSelectedFile={setSelectedFile}
          disabledRight={disabledRight ?? false}
          drawingsSearch={search}
          drawings={drawings}
          drawingsLoading={isDrawingsLoading}
        />
      </div>

      <ModalOpenPDF
        fileId={pdfModalFileId}
        fileName={pdfModalFileName}
        pageCount={EMPTY_PAGE_COUNT}
        projectId={generalStore.projectId ?? undefined}
        mode="define"
        initialPage={pdfModalPage}
        initialSelection={pdfModalHighlightArea}
        initialRotation={pdfModalRotation}
        drawingId={pdfModalDrawingId || selectedDrawingId}
        initialName={pdfModalDrawing?.fileName}
        initialPlane={pdfModalDrawing?.plane}
        isOpen={isPdfModalOpen}
        setIsOpen={setIsPdfModalOpen}
        openModal={() => setIsPdfModalOpen(true)}
        closeModal={() => setIsPdfModalOpen(false)}
      />
      <ModalBindToModel
        isOpen={isBindOpen}
        setIsOpen={setIsBindOpen}
        openModal={() => setIsBindOpen(true)}
        closeModal={() => setIsBindOpen(false)}
        drawings={toBindDrawingTargets(selectedDrawingsForBind)}
      />
      <ModalDeleteDrawing
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        openModal={() => setIsDeleteOpen(true)}
        closeModal={() => setIsDeleteOpen(false)}
      />
      <ModalDrawingInfo
        isOpen={isInfoOpen}
        setIsOpen={setIsInfoOpen}
        openModal={() => setIsInfoOpen(true)}
        closeModal={() => setIsInfoOpen(false)}
        drawing={selectedDrawing}
      />
      {isRenameOpen && selectedDrawingId && selectedDrawingName !== undefined ? (
        <ModalReName
          id={selectedDrawingId}
          type="DRAWING"
          name={selectedDrawingName}
          isOpen={isRenameOpen}
          setIsOpen={setIsRenameOpen}
          openModal={() => setIsRenameOpen(true)}
          closeModal={() => setIsRenameOpen(false)}
        />
      ) : null}
    </section>
  );
});
